
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(timezone);

import { DaterangepickerComponent, DaterangepickerDirective } from 'ngx-daterangepicker-material';

export interface DatetimeRange {
    startDatetime: dayjs.Dayjs,
    stopDatetime: dayjs.Dayjs
}

@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  standalone: false
})
export class DatetimePickerComponent implements OnChanges {
    datetimeRange: DatetimeRange;

    predefinedDateRanges: any = null;
    @Input() defaultStartDatetime: dayjs.Dayjs;
    @Input() defaultStopDatetime: dayjs.Dayjs;
    @Input() locale: any = { applyLabel: 'ok', format: 'DD MMM YYYY' };
    @Input() tooltipText: string;
    @Input() singleDatePicker: boolean = false;
    @Input() linkedCalendars: boolean = false;
    @Input() autoApply: boolean = false;
    @Input() timePicker: boolean = true;
    @Input() placeholder: string;

    @Output() selectionChange = new EventEmitter<any>();
    
    @ViewChild(DaterangepickerDirective, { static: false }) dateRangePicker: DaterangepickerDirective;
    @ViewChild(DaterangepickerComponent) dateRangePickerComponent: DaterangepickerComponent;

    ngOnInit() {
        this.setDatetimeRange();
        
        if (!this.singleDatePicker) {
            this.predefinedDateRanges = {
                'Today': [dayjs().startOf('day'), dayjs().endOf('day')],
                'Last 3 Days': [dayjs().startOf('day').subtract(2, 'days').startOf('day'), dayjs().endOf('day')],
                'Last Week': [dayjs().startOf('day').subtract(6, 'days').startOf('day'), dayjs().endOf('day')],
                'Last Month': [dayjs().startOf('day').subtract(1, 'month').add(1, 'days').startOf('day'), dayjs().endOf('day')],
                'Last 6 Months': [dayjs().startOf('day').subtract(6, 'month').add(1, 'days').startOf('day'), dayjs().endOf('day')],
                'Last Year': [dayjs().startOf('day').subtract(12, 'month').add(1, 'days').startOf('day'), dayjs().endOf('day')]
            };
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const startDatetimeChanged = changes.defaultStartDatetime && !changes.defaultStartDatetime.firstChange;
        const stopDatetimeChanged = changes.defaultStopDatetime && !changes.defaultStopDatetime.firstChange;

        if (startDatetimeChanged || stopDatetimeChanged) {
            this.setDatetimeRange();
        }
    }

    private setDatetimeRange() {
        this.datetimeRange = {
            startDatetime: this.defaultStartDatetime,
            stopDatetime: this.defaultStopDatetime ?? dayjs().endOf('day')
        }
    }

    handleOpenDateRangePicker() {
        setTimeout(() => {
            this.dateRangePicker.open();
        }, 0);
    }

    /**
     * NOTE: ngx-daterangepicker-material v6 has a bug where the timezone is not correctly applied to the selected date-range.
     * Therefore, we need to manually adjust datetime objects before emitting the selectionChange event.
     */
    onDateSelected() {
        const event = {
            startDatetime: this.fixDatetime(this.datetimeRange.startDatetime),
            stopDatetime: this.fixDatetime(this.datetimeRange.stopDatetime)
        };
        this.selectionChange.emit(event);
    }

    private fixDatetime(dt: any) {
        if (!dt) return dt;
        const tz = dayjs.tz.guess();
        let fixedDt: dayjs.Dayjs;
       
        if('tz' in dt) { // If the dt is a valid dayjs object
            fixedDt = dt.tz(tz, true);
        } else {
            fixedDt = dayjs(new Date(dt.year(), dt.month(), dt.date(), dt.hour(), dt.minute(), dt.second()), tz);
        }
        return fixedDt;
    }
}
